import { useEffect, useRef } from 'react';
import { useRowAlertContext } from '../RowAlertProvider.js';

const getTodayDateString = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

// The API sometimes emits bare `NaN` (and could emit `Infinity`/`-Infinity`) as a numeric value,
// which isn't valid JSON and makes JSON.parse throw. Swap those tokens for `null` before parsing.
const parseApiJson = (text) => JSON.parse(text.replace(/\b-?Infinity\b|\bNaN\b/g, 'null'));

// Synthesized two-tone chime so no external audio asset is needed.
const playChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContextClass();
    const playTone = (frequency, startTime, duration) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, startTime);
      gain.gain.exponentialRampToValueAtTime(0.3, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(880, now, 0.15);
    playTone(1174.66, now + 0.12, 0.2);
    setTimeout(() => ctx.close(), 500);
  } catch (err) {
    console.error('Unable to play notification sound:', err);
  }
};

// Polls `endpoint?date=<today>`, diffs each poll's rows against everything seen so far
// (keyed by `rowKey`), and pushes a dismissible toast + chime into the shared RowAlertProvider
// stack for every genuinely new row. Shared by any page-level API that wants a global "new
// row" popup (Intraday Base, Result Analysis, ...) so the polling/diff/sound logic lives in
// one place, and all sources' toasts stack in the same on-screen block instead of overlapping.
export const useNewRowAlerts = ({
  endpoint,
  rowKey,
  title,
  renderDetail,
  linkTo,
  isRowValid = (row) => Boolean(row),
  isPollingWindowOpen = () => true,
  pollIntervalMs,
  toastDurationMs = 10 * 1000,
}) => {
  const { pushToasts } = useRowAlertContext();
  const seenKeysRef = useRef(null); // null until the first fetch establishes a baseline

  const notifyNewRows = (rows) => {
    playChime();
    pushToasts(
      rows.map((row) => ({
        id: `${rowKey(row)}-${Date.now()}-${Math.random()}`,
        title,
        detail: renderDetail(row),
        linkTo,
      })),
      toastDurationMs
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!isPollingWindowOpen()) return;

      try {
        const response = await fetch(`${endpoint}?date=${getTodayDateString()}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const text = await response.text();
        const result = parseApiJson(text);
        const rows = Array.isArray(result) ? result : result.data || [];

        if (seenKeysRef.current === null) {
          // First successful fetch is the baseline - nothing is "new" yet.
          seenKeysRef.current = new Set(rows.filter(isRowValid).map(rowKey));
          return;
        }

        const newRows = rows.filter((row) => isRowValid(row) && !seenKeysRef.current.has(rowKey(row)));
        if (newRows.length === 0) return;

        newRows.forEach((row) => seenKeysRef.current.add(rowKey(row)));
        notifyNewRows(newRows);
      } catch (err) {
        console.error(`Error polling ${endpoint}:`, err);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, pollIntervalMs);
    return () => clearInterval(intervalId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
